import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import type { Probe, ProbeGroup } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * Gruppi di sonde: raggruppare per rete, per sede, per ruolo.
 *
 * I gruppi esistevano nel modello e nell'assegnazione dei job da sempre, e si
 * potevano solo **elencare**: crearli richiedeva una riga di SQL. Una funzione
 * che si vede a metà è peggio di una che non c'è — l'operatore vede il menu
 * «gruppo» quando pianifica una scansione, lo trova vuoto e non ha modo di
 * capire se è una cosa che non ha configurato o una che non esiste.
 *
 * A che cosa servono davvero: le sonde di un cliente stanno in reti diverse, e
 * quale sonda esegue una scansione decide **che cosa si vede**. Un gruppo dice
 * «una qualunque di queste va bene», che è vero fra sonde della stessa rete e
 * falso fra sonde di sedi diverse.
 */

const MODES: { value: string; label: () => string; hint: () => string }[] = [
  {
    value: 'load_balanced',
    label: () => t('Bilanciato'),
    hint: () => t('I job si distribuiscono fra tutte le sonde del gruppo.'),
  },
  {
    value: 'failover',
    label: () => t('Failover'),
    hint: () => t('Una sonda lavora, le altre subentrano se cade.'),
  },
  {
    value: 'functional',
    label: () => t('Funzionale'),
    hint: () => t('Raggruppamento per ruolo o competenza, senza distribuzione.'),
  },
]

const POLICIES: { value: string; label: () => string; hint: () => string }[] = [
  {
    value: 'least_loaded',
    label: () => t('Meno carica'),
    hint: () => t('Va alla sonda con meno job in corso. È il default e va bene quasi sempre.'),
  },
  {
    value: 'round_robin',
    label: () => t('A rotazione'),
    hint: () => t('A turno, senza guardare il carico.'),
  },
  {
    value: 'consistent_hash',
    label: () => t('Hash costante'),
    hint: () => t('Lo stesso ambito finisce sempre sulla stessa sonda: utile per confronti nel tempo.'),
  },
  {
    value: 'active_first',
    label: () => t('Prima le attive'),
    hint: () => t('Privilegia le sonde in ruolo attivo rispetto alle passive.'),
  },
]

export default function ProbeGroups() {
  const groups = useAsync(() => api.groups(), [])
  const probes = useAsync(() => api.probes(), [])
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // The groups with a change in flight: their controls wait for the answer.
  const [occupati, setOccupati] = useState<Set<string>>(new Set())

  function reload() {
    groups.reload()
    probes.reload()
  }

  const sondeDi = (g: ProbeGroup) => (probes.data ?? []).filter((p) => p.group_id === g.id)

  async function lavora(g: ProbeGroup, fn: () => Promise<unknown>) {
    setOccupati((s) => new Set(s).add(g.id))
    setError(null)
    try {
      await fn()
      reload()
    } catch (err) {
      setError(err)
    } finally {
      setOccupati((s) => {
        const n = new Set(s)
        n.delete(g.id)
        return n
      })
    }
  }

  function change(g: ProbeGroup, payload: { mode?: string; assignment_policy?: string }) {
    void lavora(g, () => api.updateGroup(g.id, payload))
  }

  function remove(g: ProbeGroup) {
    const sonde = sondeDi(g)
    // Le sonde **restano**: il vincolo è ON DELETE SET NULL. Dirlo prima evita
    // che qualcuno eviti di cancellare un gruppo temendo di disarruolare una
    // fleet — che si riarruola solo tornando fisicamente nelle reti dei clienti.
    const message =
      sonde.length > 0
        ? t('Eliminare «{nome}»? Le {n} sonde restano arruolate e tornano assegnabili a qualunque discovery del tenant. Le pianificazioni che nominavano il gruppo perdono il vincolo.', { nome: g.name, n: sonde.length })
        : t('Eliminare «{nome}»?', { nome: g.name })
    if (!window.confirm(message)) return
    void lavora(g, () => api.deleteGroup(g.id))
  }

  const colonne: Column<ProbeGroup>[] = [
    { key: 'name', header: t('Nome'), render: (g) => <strong>{g.name}</strong> },
    {
      key: 'mode',
      header: t('Modo'),
      value: (g) => MODES.find((m) => m.value === g.mode)?.label() ?? g.mode,
      render: (g) => (
        <select
          value={g.mode}
          disabled={occupati.has(g.id)}
          onChange={(e) => change(g, { mode: e.target.value })}
          style={{ width: 'auto' }}
        >
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label()}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'assignment_policy',
      header: t('Assegnazione'),
      value: (g) => POLICIES.find((p) => p.value === g.assignment_policy)?.label() ?? g.assignment_policy,
      render: (g) => (
        <select
          value={g.assignment_policy}
          disabled={occupati.has(g.id)}
          onChange={(e) => change(g, { assignment_policy: e.target.value })}
          style={{ width: 'auto' }}
        >
          {POLICIES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label()}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'sonde',
      header: t('Sonde'),
      value: (g) => sondeDi(g).map((p) => p.name).join(' ') || t('nessuna sonda'),
      render: (g) => {
        const sonde = sondeDi(g)
        const online = sonde.filter((p) => p.status === 'online').length
        // Un gruppo vuoto è la cosa che più facilmente si crea e si dimentica, ed
        // è anche quella che lascia i job in attesa per sempre. E «due sonde di
        // cui zero online» è la stessa situazione con un'apparenza diversa.
        return sonde.length === 0 ? (
          <span className="badge--warn">{t('nessuna sonda')}</span>
        ) : (
          <>
            {sonde.map((p) => (
              <Link key={p.id} to={`/fleet/${p.id}`} className="chip">
                {p.name}
              </Link>
            ))}
            {online === 0 && (
              <div className="badge--warn" style={{ fontSize: 11 }}>
                {t('nessuna in linea: i job resteranno in attesa')}
              </div>
            )}
          </>
        )
      },
    },
    {
      key: 'azioni',
      header: t('Azioni'),
      sort: false,
      searchable: false,
      render: (g) => (
        <button className="secondary" disabled={occupati.has(g.id)} onClick={() => remove(g)}>
          {t('Elimina')}
        </button>
      ),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Gruppi di sonde')}</h1>
          <p className="page__subtitle">
            {t('Quale insieme di sonde può eseguire una discovery, e con quale criterio')}
          </p>
        </div>
        <button onClick={() => setCreating((open) => !open)}>
          {creating ? t('Annulla') : t('Nuovo gruppo')}
        </button>
      </header>

      <ErrorBanner error={groups.error ?? probes.error ?? error} />

      {creating && (
        <NewGroup
          onCreated={() => {
            setCreating(false)
            reload()
          }}
          onError={setError}
        />
      )}

      <Card title={t('Gruppi ({n})', { n: groups.data?.length ?? 0 })}>
        {groups.loading && !groups.data ? (
          <Spinner />
        ) : !groups.data?.length ? (
          <EmptyState
            title={t('Nessun gruppo')}
            hint={t('Servono quando le sonde sono più di una e non vedono la stessa rete: un gruppo dice «una qualunque di queste va bene». Con una sonda sola non serve a niente.')}
          />
        ) : (
          <DataTable rows={groups.data} columns={colonne} rowKey={(g) => g.id} conteggio={{ nome: t('gruppi') }} />
        )}
      </Card>

      <Ungrouped probes={(probes.data ?? []).filter((p) => !p.group_id)} />
    </>
  )
}

function NewGroup({
  onCreated,
  onError,
}: {
  onCreated: () => void
  onError: (e: unknown) => void
}) {
  const [name, setName] = useState('')
  const [mode, setMode] = useState('load_balanced')
  const [policy, setPolicy] = useState('least_loaded')
  const [busy, setBusy] = useState(false)

  async function submit() {
    setBusy(true)
    onError(null)
    try {
      await api.createGroup({ name: name.trim(), mode, assignment_policy: policy })
      onCreated()
    } catch (err) {
      onError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo gruppo')}>
      <div className="row">
        <div className="field">
          <label htmlFor="g-name">{t('Nome')}</label>
          <input
            id="g-name"
            value={name}
            placeholder={t('es. Sede di Milano')}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="field__hint">
            {t('Conviene che dica dove guardano queste sonde: è la cosa che si deve sapere quando si sceglie un gruppo per una scansione.')}
          </p>
        </div>

        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="g-mode">{t('Modo')}</label>
          <select id="g-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label()}
              </option>
            ))}
          </select>
          <p className="field__hint">{MODES.find((m) => m.value === mode)?.hint()}</p>
        </div>

        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="g-policy">{t('Assegnazione')}</label>
          <select id="g-policy" value={policy} onChange={(e) => setPolicy(e.target.value)}>
            {POLICIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label()}
              </option>
            ))}
          </select>
          <p className="field__hint">{POLICIES.find((p) => p.value === policy)?.hint()}</p>
        </div>
      </div>

      <button onClick={submit} disabled={busy || !name.trim()}>
        {busy ? t('Creazione…') : t('Crea gruppo')}
      </button>
    </Card>
  )
}

/**
 * Le sonde fuori da ogni gruppo.
 *
 * Non è un problema — con una sola sonda i gruppi non servono — ma va visto:
 * una sonda senza gruppo non è raggiungibile da nessuna discovery che si sia
 * scelta un gruppo, e quel job resta in attesa senza che la scheda della sonda
 * dica niente.
 */
function Ungrouped({ probes }: { probes: Probe[] }) {
  if (!probes.length) return null

  return (
    <Card title={t('Sonde fuori da ogni gruppo ({n})', { n: probes.length })}>
      <p style={{ marginTop: 0 }}>
        {t('Ricevono solo le discovery che non hanno scelto un gruppo. Si assegnano a un gruppo dalla scheda della singola sonda.')}
      </p>
      {probes.map((p) => (
        <Link key={p.id} to={`/fleet/${p.id}`} className="chip">
          {p.name}
        </Link>
      ))}
    </Card>
  )
}
